import { Box, Divider, IconButton, MenuItem, Select, styled, Tooltip } from '@mui/material';
import TraceableLocation from 'assets/img/traceable-illustration.png';
import QuestionnaireFormViewOnly from 'FeatureModules/Questionnaire/Components/QuestionnaireFormViewOnly';
import Loader from 'Components/Forms/Loader';
import usePageTitle from 'hooks/usePageTitle';
import QuestionnaireItem from 'FeatureModules/Questionnaire/Components/QuestionnaireResponseTable/QuestionnaireItem';
import { ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import useQuestionnaireResponses from 'FeatureModules/Questionnaire/hooks/useQuestionnaireResponses';
import React, { FC } from 'react';
import { IPartnerRequest } from 'FeatureModules/Partner/types/partner.types';
import EmptyState from 'Components/EmptyState';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';

const Container = styled(FlexBox)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.custom.themeColors.white,
  borderRadius: 8,
  border: `1px solid ${theme.custom.themeColors.grayScale[20]}`,
}));

const QuestionnaireResponses: FC = () => {
  usePageTitle('Questionnaire responses');

  const {
    selectedQuestionnaireResponseIndex,
    questionnaire,
    questionnaireResponse,
    providedQuestionnairesRequests,
    error,
    loading,
    handleSelectResponse,
    handleSelectNextResponse,
    handleSelectPreviousResponse,
    handleAcceptRequest,
  } = useQuestionnaireResponses();

  if (loading) {
    return <Loader />;
  }

  if (error || !questionnaire) {
    return <ErrorState />;
  }

  if (!providedQuestionnairesRequests?.length || !questionnaireResponse) {
    return (
      <PageContainer>
        <PageTitle
          title="Questionnaire responses"
          goBackLabel="All questionnaires"
          goBackUrl="/questionnaires"
          titleExtension={questionnaire?.title}
        />
        <Box mt={3}>
          <EmptyState
            Icon={<img src={TraceableLocation} alt="traceable illustration" />}
            message="None of the requests have been responded to yet"
            additionalInfo="Once a partner responds, it will show up here."
          />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle
        title="Questionnaire responses"
        goBackLabel="Questionnaire overview"
        goBackUrl={`/questionnaires/${questionnaire?.id}`}
        titleExtension={questionnaire?.title}
      />

      <FlexBox mt={3} mb={2} flexDirection="column" gap={2} alignItems="flex-start">
        <FlexBox width="100%" alignItems="center" gap={2} overflow="hidden">
          <Select
            value={questionnaireResponse.id}
            onChange={e => handleSelectResponse(e.target.value)}
          >
            {providedQuestionnairesRequests?.map(request => (
              <MenuItem
                key={request.questionnaireCompanyResponseId}
                value={request.questionnaireCompanyResponseId}
              >
                {request.requestedToCompany.name}
              </MenuItem>
            ))}
          </Select>
          {providedQuestionnairesRequests.length > 1 && (
            <>
              <ThemeTypography variant="BODY_MEDIUM" sx={{ whiteSpace: 'nowrap' }}>
                {selectedQuestionnaireResponseIndex} of {providedQuestionnairesRequests.length}{' '}
                responses
              </ThemeTypography>
              <FlexBox>
                <Tooltip title="Previous response">
                  <IconButton
                    size="small"
                    disabled={selectedQuestionnaireResponseIndex === 1}
                    onClick={handleSelectPreviousResponse}
                  >
                    <Icon name="arrow-left" size="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Next response">
                  <IconButton
                    size="small"
                    disabled={
                      selectedQuestionnaireResponseIndex === providedQuestionnairesRequests?.length
                    }
                    onClick={handleSelectNextResponse}
                  >
                    <Icon name="arrow-right" size="small" />
                  </IconButton>
                </Tooltip>
              </FlexBox>
            </>
          )}
          <Divider orientation="horizontal" sx={{ width: '100%' }} />
        </FlexBox>

        <QuestionnaireItem
          actionButtonsAsText
          requestStatus={questionnaireResponse.partnerRequest?.requestStatus}
          questionnaire={questionnaire}
          partnerName={questionnaireResponse.partnerRequest?.requestedToCompany.name}
          riskLevel={questionnaireResponse.riskLevel}
          onReviewRequest={
            questionnaireResponse.partnerRequest
              ? () => handleAcceptRequest(questionnaireResponse.partnerRequest as IPartnerRequest)
              : undefined
          }
        />
      </FlexBox>

      <Container>
        <QuestionnaireFormViewOnly defaultExpanded sections={questionnaireResponse.sections} />
      </Container>
    </PageContainer>
  );
};

export default QuestionnaireResponses;
